<script>
  import { createEventDispatcher } from "svelte";
  import backend from "../../../../server";
  import Pagination from "../../Pagination.svelte";

  // ========== Const ==========
  const LIST_USERS = (params) => backend.users.list(params);
  const PAGE_SIZE = 200;
  // ========== Props ==========
  export let idStrategy = "auto";
  export let clientId = "";
  export let initialSelected = [];
  export let onCloseEsc = true;
  export let currency = "";
  export let excludeIds = [];
  export let staticPlayers = null;
  export let listUsers = LIST_USERS;
  export let confirmLabel = "Guardar";
  export let stepLabel = "";

  // ========== Setup ==========
  const dispatch = createEventDispatcher();
  const MOCK_PLAYERS = [
    // {
    //   value: "43409",
    //   label: "demo15",
    //   username: "8229576944463",
    //   realName: "demo15",
    //   clientIdOverride: "DEMOFRONT",
    //   currency: "USD",
    //   isMock: true,
    // },
    // {
    //   value: "0472985538653",
    //   label: "QADEMOUSD",
    //   username: "0472985538653",
    //   realName: "QADEMOUSD",
    //   clientIdOverride: "DEMOFRONT",
    //   currency: "USD",
    //   isMock: true,
    // },
  ];
  const norm = (s) =>
    (s ?? "")
      .toString()
      .normalize("NFD")
      .replace(/\p{Diacritic}/gu, "")
      .toLowerCase()
      .trim();

  // ========== State ==========
  let idRaw = "";
  let query = "";
  let isLoading = false;
  let players = [...MOCK_PLAYERS];
  const cache = new Map();
  let playerInfoById = new Map();
  let selected = new Set(initialSelected);
  let selectedCount = 0;
  let loadSeq = 0;
  let page = 1;
  let total = 0;
  let xpage = PAGE_SIZE;
  let isSearching = false;
  let searchTimeout;

  // ========== Reactive ==========
  $: needle = norm(query);
  $: curr = (currency ?? "").toString().trim().toUpperCase();
  $: excludeSet = new Set((excludeIds || []).map(String));
  $: visiblePlayers = players.filter(
    (p) => p.isMock || !excludeSet.has(String(p.value)),
  );
  $: byCurrency = curr
    ? visiblePlayers.filter(
        (p) => p.isMock || (p.currency || "").toUpperCase() === curr,
      )
    : visiblePlayers;
  $: filtered = needle
    ? byCurrency.filter(
        (p) =>
          norm(p.value).includes(needle) ||
          norm(p.label).includes(needle) ||
          norm(p.currency).includes(needle),
      )
    : byCurrency;
  $: allFilteredSelected =
    filtered.length > 0 && filtered.every((p) => selected.has(p.value) || (p.username && selected.has(p.username)));
  $: selectedCount = selected.size;
  $: canToggleSelect = filtered.length > 0;

  $: if (query !== undefined && clientId) {
    clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => {
      load(clientId, curr, 1, query);
    }, 400);
  }

  let snapshot = "";
  $: {
    const token = `${String(clientId)}|${curr}|${idStrategy}|${(initialSelected || []).map(String).join(",")}`;
    if (token !== snapshot) {
      snapshot = token;
      selected = new Set((initialSelected || []).map(String));
      playerInfoById = new Map();
      query = "";
      if (clientId) load(String(clientId), curr);
      else players = [...MOCK_PLAYERS];
    }
  }

  // ========== Data ==========
  async function fetchPlayers({
    clientId,
    currency,
    idStrategy,
    page,
    xpage,
    search,
  }) {
    const params = {
      page,
      xpage,
      client_id: clientId,
      ...(currency ? { currency } : {}),
      ...(search ? { search } : {}),
    };

    const res = await listUsers(params);
    const rows = res?.data?.list ?? [];
    const totalCount = Number(res?.data?.total || 0);
    const out = [];

    for (const u of rows) {
      let idRaw = "";
      if (idStrategy === "clientcode_username") {
        idRaw = String(u.client.code + "_" + u.username);
      } else {
        idRaw = String(u.id);
      }
      const username = u.username || u.username_origin || u.name || "";
      const name = u.username_origin || u.username || u.name || `user-${idRaw}`;
      out.push({
        value: idRaw,
        label: name,
        username,
        username_origin: u.username_origin || "",
        firstname: String(u.firstname || ""),
        currency: String(u.currency || ""),
        country: String(u.country || ""),
      });
    }

    return { list: out, total: totalCount };
  }

  async function load(cid, curr, targetPage = 1, searchQuery = "") {
    if (Array.isArray(staticPlayers)) {
      const list = Array.from(staticPlayers).map((p) => ({
        value: String(p.value),
        label: String(p.label || p.value),
        username: String(
          p.username || p.username_origin || p.label || p.value || "",
        ),
        username_origin: String(
          p.username_origin || p.label || p.username || p.value || "",
        ),
        firstname: String(p.firstname || ""),
        realName: String(
          p.realName ||
            p.firstname ||
            p.name ||
            p.username ||
            p.label ||
            p.username_origin ||
            p.value ||
            "",
        ),
        clientIdOverride: p.clientIdOverride,
        currency: String(p.currency || ""),
        country: String(p.country || ""),
      }));
      list.sort((a, b) =>
        a.label.localeCompare(b.label, "es", { sensitivity: "base" }),
      );
      players = list;
      list.forEach((player) => playerInfoById.set(String(player.value), player));
      total = list.length;
      return;
    }
    if (!cid) {
      players = [...MOCK_PLAYERS];
      total = MOCK_PLAYERS.length;
      return;
    }

    const cacheKey = `${cid}|${curr || "*"}|${idStrategy}|p${targetPage}|s:${searchQuery}`;
    if (cache.has(cacheKey)) {
      const cached = cache.get(cacheKey);
      players = [...cached.list];
      players.forEach((player) => playerInfoById.set(String(player.value), player));
      total = cached.total;
      page = targetPage;
      return;
    }

    const seq = ++loadSeq;
    isLoading = true;
    players = [];
    try {
      const { list, total: totalCount } = await fetchPlayers({
        clientId: cid,
        currency: curr,
        idStrategy,
        page: targetPage,
        xpage: PAGE_SIZE,
        search: searchQuery,
      });

      if (seq !== loadSeq) return;

      cache.set(cacheKey, { list, total: totalCount });
      players = [...list];
      players.forEach((player) => playerInfoById.set(String(player.value), player));
      total = totalCount;
      page = targetPage;

      // Resetear scroll al cambiar de página
      setTimeout(() => {
        const table = document.querySelector(".table");
        if (table) table.scrollTop = 0;
      }, 0);
    } catch (e) {
      console.error("Error cargando jugadores:", e);
      if (seq !== loadSeq) return;
      players = [];
      total = 0;
    } finally {
      if (seq === loadSeq) isLoading = false;
    }
  }

  //  ================= Handlers =================
  function toggle(p) {
    if (selected.has(p.value)) selected.delete(p.value);
    else if (p.username && selected.has(p.username)) selected.delete(p.username);
    else selected.add(p.value);
    selected = new Set(selected);
  }
  function selectAllOrNone() {
    if (allFilteredSelected) {
      for (const p of filtered) {
        selected.delete(p.value);
        if (p.username) selected.delete(p.username);
      }
    } else {
      for (const p of filtered) {
        selected.add(p.value);
      }
    }
    selected = new Set(selected);
  }
  function confirm() {
    const idToInfo = playerInfoById;
    dispatch("confirm", {
      players: Array.from(selected).map((id) => {
        const info = idToInfo.get(id);
        return info
          ? {
              id: info.value,
              label: info.label,
              username: info.username,
              username_origin: info.username_origin || "",
              firstname: info.firstname || "",
              realName: info.realName,
              clientIdOverride: info.clientIdOverride,
              currency: info.currency,
              country: info.country,
            }
          : { id, label: id };
      }),
      ids: Array.from(selected),
    });
  }
  function close() {
    dispatch("close");
  }
  function handleKeydown(e) {
    if (onCloseEsc && e.key === "Escape") close();
    if (e.key === "Enter") handleSearch();
  }
  function handleSearch() {
    if (!clientId) return;
    clearTimeout(searchTimeout);
    load(clientId, curr, 1, query);
  }
  function changePage(newPage) {
    if (!clientId) return;
    load(clientId, curr, newPage, query);
  }
</script>

<div
  class="modal d-block"
  tabindex="-1"
  on:keydown={handleKeydown}
  style="z-index:1060;"
>
  <div class="modal-dialog modal-xl modal-dialog-centered fixed-modal">
    <div class="modal-content modal-fixed">
      <div class="modal-header">
        <h5 class="modal-title">Seleccionar jugadores</h5>
        <button type="button" class="btn-close" on:click={close}></button>
      </div>

      <div class="modal-body modal-body-fixed">
        <div class="filters">
          <div class="filters-heading">
            <label for="find_players" class="form-label mb-0">Buscar</label>
            {#if stepLabel}<span class="step-indicator">{stepLabel}</span>{/if}
          </div>
          <div class="fcol">
            <input
              id="find_players"
              class="form-control"
              placeholder="ID o nombre…"
              bind:value={query}
              on:keydown={(e) => e.key === "Enter" && handleSearch()}
            />
          </div>
          <div class="fcol end">
            <button
              id="btnSelect"
              class="btn btn-outline-secondary btn-sm select-toggle"
              on:click={selectAllOrNone}
              disabled={!canToggleSelect}
            >
              {allFilteredSelected ? "Deseleccionar todo" : "Seleccionar todo"}
            </button>
          </div>
        </div>

        {#if filtered.length === 0 && isLoading}
          <div class="empty-block">Cargando jugadores…</div>
        {:else if filtered.length === 0}
          <div class="empty-block">Sin resultados</div>
        {:else}
          <div class="table">
            <div class="thead">
              <div class="th chk"></div>
              <div class="th id">ID Jugador</div>
              <div class="th name">Jugador</div>
              <div class="th curr">Currency</div>
            </div>
            <div class="tbody">
              {#each filtered as p (p.value)}
                <label class="tr">
                  <input
                    type="checkbox"
                    class="row-check"
                    checked={selected.has(p.value) || (p.username && selected.has(p.username))}
                    on:change={() => toggle(p)}
                  />
                  <div class="td id" title={p.value}>{p.value}</div>
                  <div class="td name" title={p.label}>{p.label}</div>
                  <div class="td curr" title={p.currency}>
                    {p.currency || "—"}
                  </div>
                </label>
              {/each}
            </div>
          </div>
        {/if}

        {#if total > xpage}
          <div class="px-3">
            <Pagination
              current={page}
              {total}
              {xpage}
              onPageClick={changePage}
            />
          </div>
        {/if}
      </div>

      <div class="modal-footer sticky-footer">
        <div class="me-auto small text-muted">
          Seleccionados: {selectedCount}
        </div>
        <button class="btn btn-secondary btn-sm" on:click={close}
          >Cancelar</button
        >
        <button class="btn btn-primary btn-sm" on:click={confirm}
          >{confirmLabel}</button
        >
      </div>
    </div>
  </div>
</div>

<style>
  .step-indicator { display: inline-flex; align-items: center; padding: 0.25rem 0.55rem; border-radius: 999px; background: #e7f1ff; color: #0d6efd; font-size: 0.75rem; font-weight: 700; white-space: nowrap; }
  .fixed-modal {
    max-width: 900px;
  }
  .modal-fixed {
    height: 80vh;
    display: flex;
    flex-direction: column;
  }
  .modal-body-fixed {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    padding-bottom: 0;
  }
  .modal-footer.sticky-footer {
    flex: 0 0 auto;
    position: sticky;
    bottom: 0;
    background: #fff;
    z-index: 1;
    border-top: 1px solid var(--bs-border-color, #e5e7eb);
    margin-top: 0;
  }
  .filters {
    display: grid;
    grid-template-columns: minmax(0, 1fr) var(--select-btn-w);
    gap: 0.5rem;
    margin-bottom: 0.5rem;
    align-items: end;
    --select-btn-w: 10rem;
  }
  .filters-heading {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
  }
  .fcol {
    min-width: 0;
  }
  .fcol.end {
    display: flex;
    align-items: end;
  }
  .select-toggle {
    width: var(--select-btn-w);
    justify-self: end;
    white-space: nowrap;
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: calc(2.375rem + 2px);
    padding: 0.375rem 0.75rem;
  }
  .empty-block {
    flex: 1;
    display: grid;
    place-items: center;
    border: 1px dashed #ddd;
    border-radius: 10px;
    color: #777;
  }
  .table {
    --row-py: 0.22rem;
    --row-px: 0.55rem;
    --head-py: 0.28rem;
    --font-size: 0.875rem;
    --col1: 28px;
    --col2: 1fr;
    --col3: 1fr;
    --col4: 92px;
    flex: 1 1 auto;
    overflow: auto;
    max-width: 100%;
    border: 1px solid var(--bs-border-color, #e5e7eb);
    border-radius: 10px;
    background: #fff;
    margin-bottom: 0;
  }
  .thead,
  .tr {
    display: grid;
    grid-template-columns: var(--col1) var(--col2) var(--col3) var(--col4);
    gap: 0.2rem;
    align-items: center;
    padding: var(--row-py) var(--row-px);
    min-width: 0;
    font-size: var(--font-size);
  }
  .thead {
    position: sticky;
    top: 0;
    z-index: 1;
    background: #fafafa;
    border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
    padding: var(--head-py) var(--row-px);
    font-weight: 600;
  }
  .tr {
    border-bottom: 1px solid #f3f3f3;
    cursor: pointer;
  }
  .tr:hover {
    background: #f7f7f7;
  }
  .th,
  .td {
    min-width: 0;
    line-height: 1.1;
  }
  .th.id,
  .th.name,
  .td.id,
  .td.name {
    text-align: left;
  }
  .th.curr,
  .td.curr {
    text-align: center;
  }
  .td.id,
  .td.name {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .row-check {
    width: 14px;
    height: 14px;
    margin: 0;
    justify-self: start;
  }
</style>
